import { Injectable } from '@angular/core';
import {
  ApiConnector,
  ApiSettings,
  protobufApi,
} from '@frontend/util/api-connector';
import { httpRetryer } from '@frontend/util/http-utilities';
import { isNotNullOrUndefined } from '@frontend/util/type-guards';
import {
  AccountFunnelTableRequest,
  AccountFunnelTableRequestQuery,
  AccountTableRow,
  DataStudioApiService,
  DataStudioMessages,
  ExportAccountFunnelTableRequest,
  GenericMessages,
  SortColumn,
  StageAccountType,
} from 'grpc-gateway-generated';
import {
  combineLatest,
  EMPTY,
  expand,
  filter,
  from,
  iif,
  map,
  reduce,
  switchMap,
  take,
} from 'rxjs';

type DataStudioEnv = {
  DATA_STUDIO_API_URL: string;
};

@Injectable({ providedIn: 'root' })
export class DataStudioAdminApi extends ApiConnector<DataStudioEnv> {
  protected baseUrl = this.env.DATA_STUDIO_API_URL;

  protected defaultSettings = ApiSettings({
    authorizationBearerToken: this.authService.actualUserHubIdpToken$.pipe(
      filter(isNotNullOrUndefined),
    ),
  });

  protected api = protobufApi(DataStudioApiService);
  getGraph = this.endpoint(this.api.GetAccountFunnelGraph, {
    modifyRequest: (request) =>
      this.authService.hubUserData$.pipe(
        map((hubUserData) => ({
          ...request,
          orgUuid: hubUserData?.orgUuid || '',
        })),
      ),
  });

  exportToList = this.endpoint(this.api.AccountFunnelAccountsToList, {
    modifyRequest: (request) =>
      this.authService.hubUserData$.pipe(
        map((hubUserData) => ({
          ...request,
          orgUuid: hubUserData?.orgUuid || '',
        })),
      ),
  });

  getTop50AccountsByAttribute = this.endpoint(
    this.api.GetTop50AccountsForSegmentByAttribute,
    {
      modifyRequest: (request) =>
        this.authService.hubUserData$.pipe(
          map((hubUserData) => ({
            ...request,
            orgUuid: hubUserData?.orgUuid || '',
          })),
        ),
    },
  );

  getIntentTopics = this.endpoint(this.api.GetIntentTopicsForSegment, {
    modifyRequest: (request) =>
      this.authService.hubUserData$.pipe(
        map((hubUserData) => ({
          query: {
            ...request.query,
            orgUuid: hubUserData?.orgUuid || '',
          },
        })),
      ),
  });

  getAccountFunnelCsvUuid = this.endpoint(this.api.ExportAccountFunnelTable, {
    modifyRequest: (request) => {
      const defaultParams: ExportAccountFunnelTableRequest = {
        accountType: StageAccountType.ALL,
        globalFilters: [],
        search: '',
        stageIds: [],
        suggestedFileName: 'Engagement_Funnel.csv',
      };
      return this.authService.hubUserData$.pipe(
        map((hubUserData) => ({
          ...defaultParams,
          ...request,
          orgUuid: hubUserData?.orgUuid || '',
        })),
      );
    },
  });

  getWeeklyActivityForSegment = this.paginatedEndpoint(
    this.api.GetWeeklyActivityForSegment,
    {
      visiblePageSize: Infinity,
      getItemsFromResponse: (response) => response.weeks,
      modifyRequest: (request) => {
        // if there is a continuationToken, return request unmodified
        if (request.continuationToken) return request;

        // pipe in the hubUser's orgUuid
        return this.authService.hubUserData$.pipe(
          map(
            (hubUserData) =>
              ({
                query: {
                  ...request.query,
                  orgUuid: hubUserData?.orgUuid || '',
                },
              } as DataStudioMessages.WeeklyActivityRequest),
          ),
        );
      },
    },
  );

  getTable = this.paginatedEndpoint(this.api.GetAccountFunnelTable, {
    getItemsFromResponse: (response) => response.rows,
    modifyRequest: (request) => {
      // if there is a continuationToken, return request unmodified
      if (request.continuationToken) return request;

      const defaultQuery: AccountFunnelTableRequest['query'] = {
        accountType: StageAccountType.ALL,
        globalFilters: [],
        search: '',
        stageIds: [],
        sortDirection: GenericMessages.SortDirection.DESC,
        sortColumn: SortColumn.IMPRESSIONS,
        pageSize: 10,
      };

      // pipe in the hubUser's orgUuid
      return this.authService.hubUserData$.pipe(
        map(
          (hubUserData) =>
            ({
              query: {
                ...defaultQuery,
                ...request.query,
                orgUuid: hubUserData?.orgUuid || '',
              },
            } as AccountFunnelTableRequest),
        ),
      );
    },
    withSearch: (searchText, request) => {
      // if there is a continuationToken, return request unmodified
      if (request.continuationToken || !searchText) return request;

      const query = request.query || {};

      return {
        ...request,
        query: {
          ...query,
          search: searchText,
        },
      } as AccountFunnelTableRequest;
    },
    withItemsCountFromResponse: (response) => {
      return Number(response.totalRows);
    },
  });

  getTotalActivity = this.endpoint(this.api.GetTotalActivityForSegment, {
    modifyRequest: (request) =>
      this.authService.hubUserData$.pipe(
        map((hubUserData) => ({
          ...request,
          orgUuid: hubUserData?.orgUuid || '',
        })),
      ),
  });

  getAllTableData(query: AccountFunnelTableRequestQuery) {
    const defaultQuery = {
      accountType: StageAccountType.ALL,
      globalFilters: [],
      search: '',
      stageIds: [],
      sortDirection: GenericMessages.SortDirection.DESC,
      sortColumn: SortColumn.IMPRESSIONS,
      pageSize: 10000,
    };
    const getTableData = (continuationToken?: string) => {
      return combineLatest([
        this.authService.hubIdpToken$,
        this.authService.hubUserData$,
      ]).pipe(
        take(1),
        switchMap(([token, userData]) =>
          from(
            DataStudioApiService.GetAccountFunnelTable(
              continuationToken
                ? { continuationToken }
                : {
                    query: {
                      ...defaultQuery,
                      ...query,
                      orgUuid: userData.orgUuid,
                    },
                  },
              {
                pathPrefix: this.baseUrl,
                headers: { authorization: `Bearer ${token}` },
              },
            ),
          ).pipe(httpRetryer({})),
        ),
      );
    };

    return getTableData().pipe(
      // expands the observable to get all follow up pages
      expand((res) =>
        iif(
          (): boolean => !!res.continuationToken,
          getTableData(res.continuationToken),
          EMPTY,
        ),
      ),
      // merges all the results, like a reduce, starting with an empty array
      reduce((acc, res) => acc.concat(res.rows || []), <AccountTableRow[]>[]),
    );
  }

  /** getting config data needs actual user token */
  getInternalConfigData = this.endpoint(this.api.GetInternalConfigData);

  /** updating config data needs actual user token */
  updateInternalConfigData = this.endpoint(this.api.UpdateInternalConfigData);
}
